<!-- /feedback (Seán, 2026-11-03): an online event's feedback on a page of its own, also reached
     from the follow-up email (?event=masterclass-2026). The same form and end state as /live
     (LiveEndState.vue), the same record (publicMetadata.events[name].feedback) and the server's
     status (api/v1/user/feedback.get.js): signed out, not a participant, not finished, open,
     given, closed, or no event. No program, no Zoom. -->

<template>
  <!-- the title, and at its right on the development servers the preview: Initial State (the
       page as it is now) or Final State (the form as after the event; nothing recorded) -->
  <div class="titleRow">
    <h3>VIZBI <i>Online</i> Feedback</h3>
    <div v-if="isDevServer && status?.event" class="segmented" role="radiogroup"
      aria-label="Preview">
      <button
        v-for="option in PREVIEW_OPTIONS"
        :key="option.label"
        type="button"
        role="radio"
        :aria-checked="String(isPreview === option.isPreview)"
        :class="{isChosen: isPreview === option.isPreview}"
        @click="isPreview = option.isPreview">
        {{ option.label }}
      </button>
    </div>
  </div>
  <div class="feedbackPage">
    <template v-if="!isSignedIn">
      <p>Please sign in to share your feedback on VIZBI <i>Online</i>.</p>
      <button type="button" class="action" @click="signIn">Sign In</button>
    </template>
    <p v-else-if="isLoading">Loading…</p>
    <template v-else-if="loadError">
      <p>Your feedback page couldn't be loaded.</p>
      <button type="button" class="action" @click="load">Try Again</button>
    </template>
    <template v-else>
      <!-- several events to give feedback on: which one -->
      <nav v-if="choices.length > 1" class="choices" aria-label="Events">
        <router-link
          v-for="choice in choices"
          :key="choice.name"
          :to="{path: '/feedback', query: {event: choice.name}}"
          :class="{isCurrent: choice.name === status.event}"
          :aria-current="choice.name === status.event ? 'page' : undefined">
          VIZBI <i>Online</i> {{ getEventType(choice.name) }}
        </router-link>
      </nav>
      <!-- Final State (development servers only): the form as it will be, nothing recorded -->
      <LiveEndState
        v-if="isAsking || isPreview"
        :key="isPreview ? 'preview' : 'real'"
        :event-name="getEventName(status.event)"
        is-plain
        :is-over="Boolean(status.hasEnded) || isPreview"
        :is-preview />
      <template v-else-if="status.state === 'not-finished'">
        <p class="paragraph">
          We'd love to hear your feedback on the VIZBI <i>Online</i>
          {{ getEventType(status.event) }}.
        </p>
        <p class="message">Please return to this page once the event has finished.</p>
      </template>
      <p v-else class="message" v-html="message" />
      <!-- before the event: the way to its page -->
      <div v-if="hasEventLink" class="buttons">
        <router-link v-if="hasEventLink" :to="eventPath" class="button actionButton">
          Go to the {{ getEventType(status.event) }}
        </router-link>
      </div>
      <p v-if="upcomingLine && isThanked" class="upcoming">
        Coming up: VIZBI <i>Online</i> {{ upcomingLine }}.
        <router-link to="/live">See it on the live page</router-link>.
      </p>
    </template>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import LiveEndState from "@/components/LiveEndState";
import useUserSafe from "@/composables/useUserSafe";
import useRoles from "@/composables/useRoles";
import {requestSignIn} from "@/composables/useSignInRequest";
import {fetchFeedbackStatus, getEventName, getEventType, getHasFeedback}
  from "@/composables/useEventFeedback";

const MESSAGES = {
  "not-participant": "Feedback is for those who took part in a VIZBI <i>Online</i> event. Thank "
    + "you for your interest!",
  "not-finished": "Feedback opens once the event has ended. Thank you for joining us!",
  closed: "Feedback for this event has closed. Thank you for joining us!",
  none: "There's no VIZBI <i>Online</i> event to give feedback on just now."
};
const [route, router] = [useRoute(), useRouter()];
const {isSignedIn, user} = useUserSafe();
const [status, isLoading, loadError] = [ref(null), ref(false), ref(false)];
const isPreview = ref(false); // Final State: the form, as after the event; nothing recorded
const PREVIEW_OPTIONS = [{label: "Initial State", isPreview: false},
  {label: "Final State", isPreview: true}];
// the preview (Initial State | Final State): development servers, admins and testers
const {isAdmin, isTester} = useRoles();
const isDevServer = computed(() => {
  return /^local|^dev|^192/.test(window.location.host) || isAdmin.value || isTester.value;
});
// each event's page (events.json's online Page)
const eventPath = computed(() => {
  const year = String(status.value?.event || "").split("-")[1];
  return status.value?.event?.startsWith("masterclass") ? `/${year}/Masterclass`
    : `/${year}/Program`;
});
// before the event: the way to its page, the one thing to do now
const hasEventLink = computed(() => status.value?.state === "not-finished" && !isPreview.value);

// open or given: the form, or the thanks once given (the end state decides, from the account)
const isAsking = computed(() => ["open", "given"].includes(status.value?.state));
const message = computed(() => MESSAGES[status.value?.state] || MESSAGES.none);
const choices = computed(() => (status.value?.events || []).filter((choice) => {
  return ["open", "given"].includes(choice.state);
}));
const isThanked = computed(() => {
  return status.value?.state === "given" || getHasFeedback(user.value,
    getEventName(status.value?.event));
});
const upcomingLine = computed(() => {
  const upcoming = status.value?.upcoming;
  if (!upcoming || upcoming.name === status.value?.event) return "";
  return `${getEventType(upcoming.name)}, ${upcoming.date}`;
});

async function load() {
  [isLoading.value, loadError.value] = [true, false];
  try {
    status.value = await fetchFeedbackStatus(String(route.query.event || ""));
  } catch (error) {
    console.error(`Feedback: ${error}`);
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

// back here once signed in (the query kept), and the watch below loads the status
function signIn() {
  requestSignIn(router, "feedback", () => {});
}

// signed in (now, or later through the dialog) or another event chosen: the status again
watch([isSignedIn, () => route.query.event], ([signedIn]) => signedIn && load(),
  {immediate: true});
</script>

<style scoped>
/* the title to what follows: 16px (global.css gives h3 8px) */
.titleRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 16px; /* ▶ EDIT HERE: space between the title and the introduction */
}
h3 {
  margin: 0;
}
/* the light segmented control (#register's Myself | Someone Else): a rounded track, the chosen
   option the light brand capsule (global.css --second-*: "you can press this"), the other plain */
.segmented {
  display: inline-flex;
  padding: 1px;
  border: 1px solid color-mix(in srgb, var(--text-color) 35%, transparent);
  border-radius: 999px;
}
.segmented button {
  min-height: 32px;
  padding: 0 1em;
  border: 1px solid transparent;
  border-radius: 999px;
  box-shadow: none;
  background: none;
  color: var(--text-color);
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
}
.segmented button:not(.isChosen):hover {
  color: var(--text-color-strong);
}
.segmented button.isChosen {
  border-color: color-mix(in srgb, var(--brand-color) var(--second-border-share),
    var(--second-border-base));
  background: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  color: color-mix(in srgb, white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color)));
  font-weight: 600;
}
.segmented button:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
.feedbackPage {
  max-width: 720px;
  text-align: left;
}
.choices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px; /* ▶ EDIT HERE: space between the events and the card */
}
.choices a {
  padding: 0.35em 0.9em;
  border: 1.5px solid color-mix(in srgb, var(--brand-color) 60%, transparent);
  border-radius: 8px;
  font-weight: 700;
  text-decoration: none;
}
.choices a.isCurrent {
  background: var(--brand-dark-color);
  border-color: var(--brand-dark-color);
  color: white;
}
.buttons {
  display: inline-grid; /* as /live's: the buttons all as wide as the widest */
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  margin-bottom: 24px; /* ▶ EDIT HERE: space between the buttons and what follows */
}
/* the gap above the buttons, from the message (the card has its own: LiveEndState.vue) */
.paragraph {
  margin: 0 0 12px; /* ▶ EDIT HERE: space between paragraphs */
}
.message {
  margin: 0 0 24px; /* ▶ EDIT HERE: space between the message and the buttons */
}
.button {
  box-sizing: border-box;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.2em;
  border: 1.5px solid color-mix(in srgb, white var(--second-text-lift), var(--brand-color));
  border-radius: 8px;
  box-shadow: none;
  background: none;
  color: color-mix(in srgb, white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color)));
  font: inherit;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.button.actionButton {
  border-color: var(--brand-dark-color);
  background: var(--brand-dark-color);
  color: white;
}
button.action {
  min-height: 44px;
  padding: 0 1.5em;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  background: var(--brand-dark-color);
  color: white;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
</style>
